import { useEffect, useState } from 'react';
import type { PropertyUploadJob } from '@metron/shared';
import { createLogger, Permission } from '@metron/shared';
import { Button } from '../components/ui/button.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.tsx';
import { Input } from '../components/ui/input.tsx';
import { Label } from '../components/ui/label.tsx';
import { Badge } from '../components/ui/badge.tsx';
import { usePropertyUploads } from '../hooks/usePropertyUploads.ts';
import { usePermissions } from '../hooks/usePermissions.ts';
import { Download, FileUp, RefreshCw } from 'lucide-react';

const logger = createLogger('PropertyUploadsPage');

const formatDate = (dateString: string) => {
  try {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Invalid date';
  }
};

const getStatusBadgeVariant = (
  status: PropertyUploadJob['status'],
): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (status) {
    case 'completed':
      return 'default';
    case 'processing':
      return 'secondary';
    case 'failed':
      return 'destructive';
    case 'pending':
      return 'outline';
    default:
      return 'outline';
  }
};

export function PropertyUploadsPage() {
  const [file, setFile] = useState<File | null>(null);
  const [customerID, setCustomerID] = useState('');
  const [jobs, setJobs] = useState<PropertyUploadJob[]>([]);
  const [selectedJob, setSelectedJob] = useState<PropertyUploadJob | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [viewMode, setViewMode] = useState<'my' | 'all'>('my');
  const {
    uploadCSV,
    listUploadHistory,
    getJobStatus,
    downloadTemplate,
    isLoading,
    error,
  } = usePropertyUploads();
  const { hasPermission } = usePermissions();

  const canViewAllUploads = hasPermission(Permission.VIEW_ALL_PROPERTY_UPLOADS);

  // Load job history on mount and when view mode changes
  useEffect(() => {
    loadJobHistory();
  }, [viewMode]);

  // Poll for job updates with exponential backoff
  useEffect(() => {
    if (!selectedJob || (selectedJob.status !== 'processing' && selectedJob.status !== 'pending')) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let pollInterval = 1000; // Start with 1 second
    const maxInterval = 30000; // Cap at 30 seconds
    const backoffMultiplier = 2; // Double the interval each time

    const poll = async () => {
      try {
        const updatedJob = await getJobStatus(selectedJob.jobId);
        setSelectedJob(updatedJob);

        // Update in the jobs list too
        setJobs((prev) => prev.map((j) => (j.jobId === updatedJob.jobId ? updatedJob : j)));

        // Stop polling if job is complete
        if (updatedJob.status === 'completed' || updatedJob.status === 'failed') {
          return;
        }

        // Exponential backoff: double the interval each time, capped at maxInterval
        pollInterval = Math.min(pollInterval * backoffMultiplier, maxInterval);

        // Schedule next poll
        timeoutId = setTimeout(poll, pollInterval);
      } catch (err) {
        logger.error({ err }, 'Error polling job status');
        // On error, retry with current interval (don't increase backoff)
        timeoutId = setTimeout(poll, pollInterval);
      }
    };

    // Start polling
    poll();

    // Cleanup function
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [selectedJob?.jobId, selectedJob?.status, getJobStatus]);

  const loadJobHistory = async () => {
    try {
      const allUsers = viewMode === 'all' && canViewAllUploads;
      const history = await listUploadHistory(allUsers);
      setJobs(history);
    } catch (err) {
      logger.error({ err }, 'Error loading job history');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadTemplate();
    } catch (err) {
      logger.error({ err }, 'Error downloading template');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file || !customerID) {
      return;
    }

    const custID = parseInt(customerID, 10);
    if (isNaN(custID) || custID <= 0) {
      alert('Please enter a valid customer ID');
      return;
    }

    setIsUploading(true);
    try {
      const result = await uploadCSV(file, custID);

      // Reload job history
      await loadJobHistory();

      // Get the full job details and select it
      const job = await getJobStatus(result.jobId);
      setSelectedJob(job);

      // Clear form
      setFile(null);
      setCustomerID('');
      const fileInput = document.getElementById('file-input') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (err) {
      logger.error({ err }, 'Upload error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleJobClick = async (job: PropertyUploadJob) => {
    try {
      const fullJob = await getJobStatus(job.jobId);
      setSelectedJob(fullJob);
    } catch (err) {
      logger.error({ err }, 'Error loading job details');
    }
  };

  return (
    <div className='container mx-auto py-6'>
      <div className='mb-6'>
        <h1 className='text-3xl font-bold tracking-tight'>Property CSV Upload</h1>
        <p className='text-muted-foreground mt-2'>
          Upload property CSV files to create and associate properties with sensor groups
        </p>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        {/* Upload Form */}
        <Card>
          <CardHeader>
            <CardTitle>Upload CSV</CardTitle>
            <CardDescription>
              Select a CSV file and specify the customer ID to begin processing
            </CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div>
              <Label htmlFor='file-input'>CSV File</Label>
              <Input
                id='file-input'
                type='file'
                accept='.csv'
                onChange={handleFileChange}
                disabled={isUploading}
              />
              {file && (
                <p className='text-sm text-muted-foreground mt-1'>
                  Selected: {file.name}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor='customer-id'>Customer ID</Label>
              <Input
                id='customer-id'
                type='number'
                placeholder='Enter customer ID'
                value={customerID}
                onChange={(e) => setCustomerID(e.target.value)}
                disabled={isUploading}
              />
            </div>

            <div className='flex gap-2'>
              <Button
                onClick={handleUpload}
                disabled={!file || !customerID || isUploading}
                className='flex-1'
              >
                <FileUp className='mr-2 h-4 w-4' />
                {isUploading ? 'Uploading...' : 'Upload & Process'}
              </Button>
              <Button
                onClick={handleDownloadTemplate}
                variant='outline'
              >
                <Download className='mr-2 h-4 w-4' />
                Template
              </Button>
            </div>

            {error && <p className='text-sm text-destructive'>{error}</p>}
          </CardContent>
        </Card>

        {/* Job History */}
        <Card>
          <CardHeader className='flex flex-row items-center justify-between'>
            <div>
              <CardTitle>Upload History</CardTitle>
              <CardDescription>
                Recent property upload jobs
              </CardDescription>
            </div>
            <Button
              variant='ghost'
              size='sm'
              onClick={loadJobHistory}
              disabled={isLoading}
            >
              <RefreshCw className='h-4 w-4' />
            </Button>
          </CardHeader>
          <CardContent className='space-y-4'>
            {/* View Mode Toggle - Only visible to admins */}
            {canViewAllUploads && (
              <div className='flex gap-2 p-1 bg-muted rounded-md'>
                <Button
                  variant={viewMode === 'my' ? 'default' : 'ghost'}
                  size='sm'
                  className='flex-1'
                  onClick={() => setViewMode('my')}
                >
                  My Uploads
                </Button>
                <Button
                  variant={viewMode === 'all' ? 'default' : 'ghost'}
                  size='sm'
                  className='flex-1'
                  onClick={() => setViewMode('all')}
                >
                  All Uploads
                </Button>
              </div>
            )}

            <div className='space-y-2 max-h-96 overflow-y-auto'>
              {jobs.length === 0
                ? (
                  <p className='text-sm text-muted-foreground text-center py-8'>
                    No uploads yet
                  </p>
                )
                : (
                  jobs.map((job) => (
                    <button
                      type='button'
                      key={job.jobId}
                      onClick={() => handleJobClick(job)}
                      className='w-full text-left p-3 rounded-md border hover:bg-accent transition-colors'
                    >
                      <div className='flex items-center justify-between'>
                        <div className='flex-1'>
                          <p className='font-medium text-sm'>{job.fileName}</p>
                          <p className='text-xs text-muted-foreground'>
                            Customer: {job.customerID}
                            {viewMode === 'all' && ` • Uploaded by: ${job.userEmail}`}
                            {' • '}
                            {formatDate(job.createdAt)}
                          </p>
                        </div>
                        <Badge variant={getStatusBadgeVariant(job.status)}>
                          {job.status}
                        </Badge>
                      </div>
                      {job.status === 'processing' && (
                        <div className='mt-2'>
                          <div className='w-full bg-secondary rounded-full h-2'>
                            <div
                              className='bg-primary h-2 rounded-full transition-all'
                              style={{ width: `${job.progress}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </button>
                  ))
                )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Job Details */}
      {selectedJob && (
        <Card className='mt-6'>
          <CardHeader>
            <div className='flex items-center justify-between'>
              <div>
                <CardTitle>{selectedJob.fileName}</CardTitle>
                <CardDescription>
                  Job ID: {selectedJob.jobId}
                  {viewMode === 'all' && ` • Uploaded by: ${selectedJob.userEmail}`}
                </CardDescription>
              </div>
              <Badge variant={getStatusBadgeVariant(selectedJob.status)} className='text-base'>
                {selectedJob.status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className='space-y-4'>
            {/* Progress */}
            {(selectedJob.status === 'processing' || selectedJob.status === 'pending') && (
              <div>
                <div className='flex justify-between text-sm mb-2'>
                  <span>Progress</span>
                  <span>{selectedJob.progress}%</span>
                </div>
                <div className='w-full bg-secondary rounded-full h-2'>
                  <div
                    className='bg-primary h-2 rounded-full transition-all'
                    style={{ width: `${selectedJob.progress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Statistics */}
            <div className='grid grid-cols-2 md:grid-cols-5 gap-4'>
              <div>
                <p className='text-sm text-muted-foreground'>Total</p>
                <p className='text-2xl font-bold'>{selectedJob.totalRecords}</p>
              </div>
              <div>
                <p className='text-sm text-muted-foreground'>Processed</p>
                <p className='text-2xl font-bold'>{selectedJob.processedRecords}</p>
              </div>
              <div>
                <p className='text-sm text-muted-foreground'>Created</p>
                <p className='text-2xl font-bold text-green-600'>{selectedJob.createdRecords}</p>
              </div>
              <div>
                <p className='text-sm text-muted-foreground'>Updated</p>
                <p className='text-2xl font-bold text-blue-600'>{selectedJob.updatedRecords}</p>
              </div>
              <div>
                <p className='text-sm text-muted-foreground'>Skipped</p>
                <p className='text-2xl font-bold text-orange-600'>{selectedJob.skippedRecords}</p>
              </div>
            </div>

            {/* Logs */}
            <div>
              <h3 className='font-semibold mb-2'>Logs</h3>
              <div className='bg-muted p-4 rounded-md max-h-96 overflow-y-auto font-mono text-sm space-y-1'>
                {selectedJob.logs.map((log, index) => (
                  <div
                    key={index}
                    className={`${
                      log.level === 'error'
                        ? 'text-red-600'
                        : log.level === 'warn'
                        ? 'text-orange-600'
                        : 'text-foreground'
                    }`}
                  >
                    <span className='text-muted-foreground text-xs'>
                      [{new Date(log.timestamp).toLocaleTimeString()}]
                    </span>{' '}
                    {log.message}
                  </div>
                ))}
              </div>
            </div>

            {/* Errors */}
            {selectedJob.errors.length > 0 && (
              <div>
                <h3 className='font-semibold mb-2 text-destructive'>Errors</h3>
                <div className='bg-destructive/10 p-4 rounded-md space-y-1'>
                  {selectedJob.errors.map((error, index) => (
                    <p key={index} className='text-sm text-destructive'>
                      • {error}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
